import {
  KPI,
  KpiEventContext,
  KpiTransaction,
  trackKpiEnd,
  trackKpiStart,
} from '@frontend/logrocket';
import Session, {
  APPLICATION_NAME,
  ORIGIN_SOURCE,
  ORIGIN_TRANSACTION_TYPE,
} from './Session';

// Re-exported so call sites import KPI alongside kpiStart/kpiEnd from one place.
export { KPI };

const getFlowContext = (): KpiEventContext => ({
  originSource: Session.get(ORIGIN_SOURCE) || 'unknown',
  transactionType: Session.get(ORIGIN_TRANSACTION_TYPE) || 'unknown',
  sourceApplication: Session.get(APPLICATION_NAME) || 'unknown',
});

// Transactions with an open start. kpiEnd is a no-op unless a matching kpiStart
// fired, so ends can be placed at UI-signal points (loader close, screen render)
// that also run on unrelated flows without emitting orphan _End events.
const openTransactions = new Set<KpiTransaction>();

export const kpiStart = (
  transaction: KpiTransaction,
  context: KpiEventContext = {},
): void => {
  openTransactions.add(transaction);
  trackKpiStart(transaction, { ...getFlowContext(), ...context });
};

export const kpiEnd = (
  transaction: KpiTransaction,
  context: KpiEventContext = {},
): void => {
  if (!openTransactions.delete(transaction)) return;
  trackKpiEnd(transaction, { ...getFlowContext(), ...context });
};

// The initial partner-ui load spans from getInitData (origins) through the first
// results/summary render. Its end is reached from whichever flow renders first
// (qualify or lock summary), so guard it to fire exactly once.
let initialLoadComplete = false;

export const isInitialLoadComplete = (): boolean => initialLoadComplete;

export const endInitialLoad = (context: KpiEventContext = {}): void => {
  if (initialLoadComplete) return;
  initialLoadComplete = true;
  kpiEnd(KPI.InitialPartnerUiLoad, context);
};

// Marks the initial load complete WITHOUT recording an end - used for buy/sell
// landings, which are measured by SelectBuySellSide instead. The unpaired
// _Start is ignored by "Time Between Events" / funnel metrics (they require both
// events), so no InitialPartnerUiLoad duration is recorded for those sessions.
export const discardInitialLoad = (): void => {
  if (initialLoadComplete) return;
  initialLoadComplete = true;
  openTransactions.delete(KPI.InitialPartnerUiLoad);
};
